import React from 'react';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../../shared/AppSelect';

const wizardSelectTrigger = `${APP_SELECT_FILTER_TRIGGER} px-4 py-2 rounded-lg`;

const COMPANY_TYPE_OPTIONS = [
  { value: '', label: 'Select Company Type' },
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'private_limited', label: 'Private Limited' },
  { value: 'public_limited', label: 'Public Limited' },
  { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
  { value: 'one_person_company', label: 'One Person Company' },
  { value: 'other', label: 'Other' },
];

const BUSINESS_TYPE_OPTIONS = [
  { value: '', label: 'Select Business Type' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'trading', label: 'Trading' },
  { value: 'service', label: 'Service' },
  { value: 'retailer', label: 'Retailer' },
  { value: 'wholesaler', label: 'Wholesaler' },
  { value: 'consultancy', label: 'Consultancy' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'other', label: 'Other' },
];

const GST_TYPE_OPTIONS = [
  { value: '', label: 'Select GST Type' },
  { value: 'Regular', label: 'Regular' },
  { value: 'Composition', label: 'Composition' },
  { value: 'Unregistered', label: 'Unregistered' },
];

const CURRENCY_OPTIONS = [
  { value: 'INR', label: 'Indian Rupee (INR)' },
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
];

const BasicInfoStep = ({ data, errors, onChange, onTouch }) => {
  const handleChange = (field, value) => {
    onChange(field, value);
    onTouch(field);
  };

  const selectCls = (hasError) =>
    `${wizardSelectTrigger} ${hasError ? 'border-red-500' : 'border-gray-300'}`;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Basic Company Information</h2>
      
      {/* Company Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Company/Firm Name *
        </label>
        <input
          type="text"
          value={data.firmName || ''}
          onChange={(e) => handleChange('firmName', e.target.value)}
          onBlur={() => onTouch('firmName')}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.firmName ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter company/firm name"
        />
        {errors.firmName && <p className="text-red-500 text-sm mt-1">{errors.firmName}</p>}
      </div>

      {/* Company Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Company Type *
        </label>
        <AppSelect
          value={data.companyType || ''}
          onChange={(e) => handleChange('companyType', e.target.value)}
          aria-label="Company type"
          sheetTitle="Company type"
          options={COMPANY_TYPE_OPTIONS}
          triggerClassName={selectCls(errors.companyType)}
        />
        {errors.companyType && <p className="text-red-500 text-sm mt-1">{errors.companyType}</p>}
        
        {data.companyType === 'other' && (
          <input
            type="text"
            value={data.companyTypeOther || ''}
            onChange={(e) => handleChange('companyTypeOther', e.target.value)}
            onBlur={() => onTouch('companyTypeOther')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-2"
            placeholder="Specify company type"
          />
        )}
      </div>

      {/* Business Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Business Type *
        </label>
        <AppSelect
          value={data.businessType || ''}
          onChange={(e) => handleChange('businessType', e.target.value)}
          aria-label="Business type"
          sheetTitle="Business type"
          options={BUSINESS_TYPE_OPTIONS}
          triggerClassName={selectCls(errors.businessType)}
        />
        {errors.businessType && <p className="text-red-500 text-sm mt-1">{errors.businessType}</p>}
      </div>

      {/* Contact Person */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Contact Person *
        </label>
        <input
          type="text"
          value={data.contactPerson || ''}
          onChange={(e) => handleChange('contactPerson', e.target.value)}
          onBlur={() => onTouch('contactPerson')}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.contactPerson ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter contact person name"
        />
        {errors.contactPerson && <p className="text-red-500 text-sm mt-1">{errors.contactPerson}</p>}
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Address *
        </label>
        <input
          type="email"
          value={data.email || ''}
          onChange={(e) => handleChange('email', e.target.value)}
          onBlur={() => onTouch('email')}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter email address"
        />
        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
      </div>

      {/* Phone Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number *
        </label>
        <input
          type="tel"
          value={data.contactNumber || ''}
          onChange={(e) => handleChange('contactNumber', e.target.value)}
          onBlur={() => onTouch('contactNumber')}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.contactNumber ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter phone number"
        />
        {errors.contactNumber && <p className="text-red-500 text-sm mt-1">{errors.contactNumber}</p>}
      </div>

      {/* PAN Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          PAN Number
        </label>
        <input
          type="text"
          value={data.pan || ''}
          onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
          onBlur={() => onTouch('pan')}
          maxLength="10"
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.pan ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter PAN number (e.g., ABCDE1234F)"
        />
        {errors.pan && <p className="text-red-500 text-sm mt-1">{errors.pan}</p>}
      </div>

      {/* GST Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          GST Registration Type
        </label>
        <AppSelect
          value={data.gstinType || ''}
          onChange={(e) => handleChange('gstinType', e.target.value)}
          aria-label="GST registration type"
          sheetTitle="GST type"
          options={GST_TYPE_OPTIONS}
          triggerClassName={selectCls(errors.gstinType)}
        />
        {errors.gstinType && <p className="text-red-500 text-sm mt-1">{errors.gstinType}</p>}
      </div>

      {/* GSTIN Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          GSTIN Number
        </label>
        <input
          type="text"
          value={data.gstin || ''}
          onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
          onBlur={() => onTouch('gstin')}
          maxLength="15"
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.gstin ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter GSTIN number (15 characters)"
          disabled={data.gstinType === 'Unregistered'}
        />
        {errors.gstin && <p className="text-red-500 text-sm mt-1">{errors.gstin}</p>}
        {data.gstinType === 'Unregistered' && (
          <p className="text-gray-500 text-sm mt-1">GSTIN not required for unregistered businesses</p>
        )}
      </div>

      {/* Currency */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Currency
        </label>
        <AppSelect
          value={data.currency || 'INR'}
          onChange={(e) => handleChange('currency', e.target.value)}
          aria-label="Currency"
          sheetTitle="Currency"
          options={CURRENCY_OPTIONS}
          triggerClassName={`${wizardSelectTrigger} border-gray-300`}
        />
      </div>
    </div>
  );
};

export default BasicInfoStep;
